import { TintError } from '../tint-error';
import LongPollingService, { PollingObserver } from './long-polling.service';
import ApiService, {
  IGetPostsReturnValue,
  IGetPostRequestReturnValue,
  IGetAggregationsReturnValue,
  IQueryParam,
  ISinglePost,
  PublicPostBody,
  ReviewBody,
  TintInclude,
} from './api.service';
import { AxiosResponse } from 'axios';
import { ISocialFeedsGroupedItem, serializeSocialFeeds } from '../mappers/map-social-feeds';
import { IPackedPersonalization, IPersonalization, IPersonalizationData } from '../types/Personalization';
import { IPost, IPostRelationships, SocialFeedsTypesMap, IAggregation } from '../index';
import { ISocialFeed } from '../types/SocialFeed';
import defaultPersonalization from '../default-personalization';

export interface ITintMeta {
  id: string;
  slug: string;
}

export interface ITintConfig {
  network?: string;
  defaultQueryCount?: number;
  defaultQuery?: string;
  personalizationId?: number;
  tags?: string;
  saved_filter_id?: number;
  from?: number;
  product_sku?: string;
}

export interface TintRunResult {
  postsResponse: {
    posts: ReadonlyArray<IPost>;
    postsRelationships?: ReadonlyArray<IPostRelationships>;
    links: {
      first: string;
      self: string;
      next?: string;
      previous?: string;
    };
  };
  aggregations?: {
    aggregations: ReadonlyArray<IAggregation>;
  };
  personalization: IPersonalization<IPersonalizationData>;
  socialFeeds: SocialFeedsTypesMap<readonly ISocialFeedsGroupedItem[]>;
  meta: ITintMeta;
  pollingObserver: PollingObserver | undefined;
}

export default class TintService {
  private readonly tintSlug: string;
  private readonly config: ITintConfig;
  private readonly apiService: ApiService;

  private realTimeService?: LongPollingService;

  constructor(tintSlug: string, config?: ITintConfig) {
    this.tintSlug = tintSlug;
    this.config = config || {};

    if (typeof tintSlug === 'undefined') {
      throw new TintError({
        message: 'You have to provide tint slug.',
      });
    }
    this.apiService = new ApiService(tintSlug);
  }

  /**
   * Fetch the single post.
   *
   * @param id - The post id
   * @returns The promise of {@link ISinglePost}
   *
   */
  public getSinglePost(id: string): Promise<ISinglePost> {
    return this.apiService.getSinglePost(id);
  }

  /**
   * Fetch the posts.
   *
   * @param config - The config object of query: {@link IQueryParam}
   * @returns The promise of posts: {@link IGetPostsReturnValue}
   *
   */
  public getPosts(config?: IQueryParam): Promise<IGetPostsReturnValue> {
    return this.apiService.getPosts(config || {});
  }

  /**
   * Fetch the aggregations.
   *
   * @returns The promise of aggregations: {@link IGetAggregationsReturnValue}
   *
   */
  public getAggregations(): Promise<IGetAggregationsReturnValue> {
    return this.apiService.getAggregations();
  }

  /**
   * Fetch the posts from url - using for pagination.
   *
   * @param pageUrl - The config object of query: {@link IQueryParam}
   * @returns The promise of posts: {@link IGetPostsReturnValue}
   *
   */
  public getPage(pageUrl: string): Promise<IGetPostsReturnValue> {
    return this.apiService.getPage(pageUrl);
  }

  /**
   * Post the public post.
   *
   * @param payload - The payload: {@link PublicPostBody}
   * @returns The promise of AxiosResponse
   *
   */
  public addPublicPost(payload: PublicPostBody): Promise<AxiosResponse> {
    return this.apiService.postPublicPost(payload);
  }

  /**
   * Post the review .
   *
   * @param payload - The payload: {@link ReviewBody}
   * @returns The promise of AxiosResponse
   *
   */
  public addReview(payload: ReviewBody): Promise<AxiosResponse> {
    return this.apiService.postReview(payload);
  }

  /**
   * Get the public post request status.
   *
   * @param requestId - The request id
   * @returns The promise of AxiosResponse
   *
   */
  public async getPublicPostRequest(requestId: string): Promise<AxiosResponse<IGetPostRequestReturnValue>> {
    return this.apiService.getPublicPostRequest(requestId);
  }

  /**
   * Get social feeds
   *
   * @returns The promise of {@link SocialFeedsTypesMap<readonly ISocialFeedsGroupedItem[]}
   *
   */
  public async getSocialFeeds(): Promise<SocialFeedsTypesMap<readonly ISocialFeedsGroupedItem[]>> {
    const tintResponse = await this.apiService.getTintBasicData(['social_feeds']);
    return serializeSocialFeeds(
      tintResponse.data.included.filter(inclusion => inclusion.type === 'social_feed') as readonly ISocialFeed[]
    );
  }

  /**
   * Fetch all basic data from tint: posts, social-feeds, personalization, webSocketObserver.
   *
   * @param config - post config {@link IQueryParam}
   *
   * @returns The promise of {@link TintRunResult}
   *
   */
  public async up(config?: IQueryParam): Promise<TintRunResult> {
    const includes: readonly TintInclude[] = !this.config.personalizationId
      ? ['social_feeds', 'personalizations', 'default_personalization']
      : ['social_feeds', 'personalizations'];
    const tintResponse = await this.apiService.getTintBasicData(includes);
    const {
      data: {
        data: {
          id,
          attributes: { slug },
          links,
        },
      },
    } = tintResponse;

    const postsResponse = await this.getPosts({
      type: this.config.network,
      size: this.config.defaultQueryCount,
      q: this.config.defaultQuery,
      tag: this.config.tags,
      saved_filter_id: this.config.saved_filter_id,
      product_sku: this.config.product_sku,
      published_at: this.config.from,
      ...config,
    });

    const personalizationData: IPersonalization<IPersonalizationData | IPackedPersonalization> = !this.config
      .personalizationId
      ? tintResponse.data.included.find(inclusion => inclusion.type === 'personalization')
      : (await this.apiService.getPersonalization(this.config.personalizationId)).data.data;

    const responseSocialFeed = tintResponse.data.included.filter(
      inclusion => inclusion.type === 'social_feed'
    ) as readonly ISocialFeed[];

    const socialFeeds = serializeSocialFeeds(responseSocialFeed);
    const pollingObserver = this.pollingServiceObservable(links.websocket);

    const personalization: IPersonalization<IPersonalizationData> = this.mapToPersonalization(personalizationData);

    const aggregations = personalization.attributes?.type === 'reviews' ? await this.getAggregations() : undefined;

    const meta = {
      id,
      slug,
    };
    return {
      postsResponse,
      aggregations,
      socialFeeds,
      meta,
      personalization,
      pollingObserver,
    };
  }

  private pollingServiceObservable(websocketLink?: string): PollingObserver | undefined {
    if (typeof websocketLink === 'string') {
      this.realTimeService = new LongPollingService();
      return this.realTimeService.startListening<IGetPostsReturnValue>(() =>
        this.apiService.getPosts({
          type: this.config.network,
          size: this.config.defaultQueryCount ? this.config.defaultQueryCount : 100,
          q: this.config.defaultQuery,
          saved_filter_id: this.config.saved_filter_id,
          product_sku: this.config.product_sku,
        })
      );
    }
    return undefined;
  }

  private isNewPersonalization(
    personalizationData: IPackedPersonalization | IPersonalizationData
  ): personalizationData is IPackedPersonalization {
    return (personalizationData as IPackedPersonalization).published !== undefined;
  }

  private mapToPersonalization = (
    personalization: IPersonalization<IPackedPersonalization | IPersonalizationData>
  ): IPersonalization<IPersonalizationData> => {
    if (!personalization) {
      return (defaultPersonalization as unknown) as IPersonalization;
    }
    if (this.isNewPersonalization(personalization.attributes.data)) {
      return {
        ...personalization,
        attributes: { ...personalization.attributes, data: personalization.attributes.data.published },
      };
    }
    return personalization as IPersonalization<IPersonalizationData>;
  };
}
